<template>
  <div class="stat-tile">
    <div class="stat-tile__icon" v-if="hasIcon">
      <slot name="icon">
        <component :is="icon" v-if="icon" :size="20" weight="bold" />
      </slot>
    </div>
    <div class="stat-tile__body">
      <div class="stat-tile__label-row">
        <span
          v-if="tone !== 'default'"
          class="stat-tile__dot"
          :class="`stat-tile__dot--${tone}`"
          aria-hidden="true"
        />
        <span class="stat-tile__label">{{ label }}</span>
      </div>
      <div class="stat-tile__value">
        <slot>{{ value }}</slot>
      </div>
      <div v-if="subValue" class="stat-tile__sub">{{ subValue }}</div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Component } from 'vue';
import { computed, useSlots } from 'vue';

const props = withDefaults(
  defineProps<{
    label: string;
    value?: string | number | null;
    subValue?: string | null;
    icon?: Component | null;
    /** `default` renders no dot; otherwise shows a colored status dot before the label. */
    tone?: 'default' | 'good' | 'warning' | 'bad';
  }>(),
  {
    value: '',
    subValue: '',
    icon: null,
    tone: 'default',
  },
);

const slots = useSlots();
const hasIcon = computed(() => Boolean(slots.icon) || Boolean(props.icon));
</script>

<style lang="scss" scoped>
.stat-tile {
  background: var(--bui-tbl-row-fill, #fffcfd);
  border-radius: var(--bui-tbl-card-radius, 12px);
  padding: 0.875rem 1rem;
  box-shadow: var(--bui-tbl-card-shadow, 0 8px 32px rgba(75, 24, 134, 0.16));
  color: var(--bui-tbl-row-text, #2a2969);
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  min-height: 76px;
  min-width: 0;
}

.stat-tile__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--bui-badge-tag-fill, #ffd9e6);
  color: var(--bui-badge-tag-text, #a420c8);
  flex-shrink: 0;
  margin-top: 2px;
}

.stat-tile__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1 1 auto;
}

.stat-tile__label-row {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.stat-tile__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  background: color-mix(in srgb, var(--bui-tbl-detail-text, #5c5c66) 45%, transparent);

  &--good {
    /* BUI ok badge: lemonlime fill */
    background: var(--bui-cta-pri-fill, #baff5c);
    box-shadow: 0 0 0 3px rgba(186, 255, 92, 0.28);
  }

  &--warning {
    background: var(--c-bui-c-warning, #ffc05c);
    box-shadow: 0 0 0 3px rgba(255, 192, 92, 0.28);
  }

  &--bad {
    /* BUI bad badge: orchid fill */
    background: var(--bui-badge-bad-fill, #de3da9);
    box-shadow: 0 0 0 3px rgba(222, 61, 169, 0.28);
  }
}

.stat-tile__label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--bui-tbl-detail-text, #5c5c66);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.stat-tile__value {
  font-size: 1rem;
  font-weight: 700;
  color: var(--bui-tbl-row-text, #2a2969);
  line-height: 1.25;
  word-break: break-word;
}

.stat-tile__sub {
  font-size: 0.75rem;
  color: var(--bui-tbl-detail-text, #5c5c66);
  line-height: 1.3;
}
</style>
